import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  TaskGroupMembersCollection,
  User,
  UsersCollection,
} from '@freelancer/datastore/collections';
import { TimeUtils } from '@freelancer/time-utils';
import type { SearchItem } from '@freelancer/ui/search';
import { SearchComponent } from '@freelancer/ui/search';
import { AvatarSize } from '@freelancer/ui/user-avatar';
import { UntilDestroy } from '@ngneat/until-destroy';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { NO_ASSIGNEE } from '../task-lists-common.types';

@UntilDestroy({ className: 'TaskAssigneeSelectComponent' })
@Component({
  standalone: false,
  selector: 'app-task-assignee-select',
  template: `
    <fl-search
      #searchComponent
      flTrackingLabel="Show Search Result"
      flOutsideClick
      placeholder="Search for assignee"
      i18n-placeholder="Task assignee select placeholder"
      [increaseZIndex]="true"
      [initialValue]="initialValue"
      [displayResultsWithEmptyInput]="showSearchResult$ | flAsync"
      [displayResults]="showSearchResult$ | flAsync"
      [itemTemplates]="{ candidate: candidateTemplateRef }"
      [searchResults]="searchResults$ | flAsync"
      [showIcon]="false"
      [scrollableSearchResults]="true"
      [roundCorners]="false"
      (click)="clickedSearch()"
      (query)="handleQuery($event)"
      (clear)="handleClear()"
      (outsideClick)="handleOutsideClick()"
    >
      <ng-template
        #candidateTemplateRef
        let-candidate
      >
        @if (candidate != -1) {
          <div
            class="CandidateListItem"
            flTrackingLabel="Choose Assignee"
            flTrackingReferenceType="assignee_id"
            [flTrackingReferenceId]="candidate.id"
            (click)="chooseAssignee(candidate, $event)"
          >
            <fl-user-avatar
              class="mr-xsmall"
              [size]="AvatarSize.SMALL"
              [users]="[candidate]"
            ></fl-user-avatar>
            <p class="AssigneeName font-bold mr-xsmall">
              {{ candidate.displayName }}
            </p>
            <p class="AssigneeName">&#64;{{ candidate.username }}</p>
          </div>
        }
      </ng-template>
    </fl-search>
  `,
  styleUrls: ['./task-assignee-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TaskAssigneeSelectComponent implements OnChanges, OnInit {
  private datastore = inject(Datastore);
  private timeUtils = inject(TimeUtils);

  AvatarSize = AvatarSize;

  @Input() taskGroupId$: Observable<number>;
  @Input() focusSearch: boolean;
  @Input() initialValue = '';

  @Output() assigneeIdEvent = new EventEmitter<number>();

  @ViewChild('searchComponent')
  searchComponent: SearchComponent;

  private showSearchResultSubject$ = new BehaviorSubject<boolean>(false);
  showSearchResult$ = this.showSearchResultSubject$.asObservable();

  private usernameSearchSubject$ = new BehaviorSubject<string | undefined>(
    undefined,
  );

  assigneeCandidates$: Observable<readonly User[]>;
  searchResults$: Observable<readonly SearchItem[]>;

  ngOnChanges(changes: SimpleChanges): void {
    if ('initialValue' in changes && this.initialValue !== '') {
      this.usernameSearchSubject$.next(this.initialValue);
    }
  }

  ngOnInit(): void {
    const assigneeUserIds$ = this.datastore
      .collection<TaskGroupMembersCollection>('taskGroupMembers', query =>
        this.usernameSearchSubject$.asObservable().pipe(
          startWith(undefined),
          this.timeUtils.rxDebounceTime(500),
          map(username =>
            username
              ? query
                  .where('taskGroupId', '==', this.taskGroupId$)
                  .search({ usernameSearch: username })
              : query.where('taskGroupId', '==', this.taskGroupId$),
          ),
        ),
      )
      .valueChanges()
      .pipe(map(users => users.map(user => user.userId)));

    this.assigneeCandidates$ = this.datastore
      .collection<UsersCollection>('users', query =>
        query.where('id', 'in', assigneeUserIds$),
      )
      .valueChanges();

    this.searchResults$ = this.assigneeCandidates$.pipe(
      map(candidates =>
        candidates.map(candidate => ({
          type: 'candidate',
          context: candidate,
        })),
      ),
      startWith([]),
    );
  }

  handleQuery(username: string): void {
    this.usernameSearchSubject$.next(username);

    if (this.initialValue !== username) {
      this.showSearchResultSubject$.next(true);
    }
  }

  handleClear(): void {
    this.assigneeIdEvent.emit(NO_ASSIGNEE);
  }

  clickedSearch(): void {
    this.showSearchResultSubject$.next(true);
  }

  handleOutsideClick(): void {
    this.showSearchResultSubject$.next(false);
  }

  chooseAssignee(assignee: User, event: MouseEvent): void {
    event.stopPropagation();
    this.assigneeIdEvent.emit(assignee.id);
    this.searchComponent.setQuery(assignee.username);
    this.showSearchResultSubject$.next(false);
  }
}
